import PropTypes from 'prop-types';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { WeezHeader, Button, TooltipHelp, Card, ToastContext } from '@weezevent/nacre';
import PartnerBatchBookingsInfos from './PartnerBatchBookingsInfos';
import PartnerBatchBookingsInventories from './PartnerBatchBookingsInventories';
import PartnerBatchBookingsRates from './PartnerBatchBookingsRates';
import HiddenText from './HiddenText';
import PartnerBatchBookingsSeat from './partnerSeat';
import { useTranslation } from 'react-i18next';
import { useForm, requiredField } from '../../utils/validators';
import PartnerBatchBooking from '../../models/partnerBatchBooking';
import * as Sentry from '@sentry/browser';
import BatchModal from './batchModal';
import Loading from '../../components/loading';
import { default as PartnerModel } from '../../models/partner';

export default function PartnerBatchBookings({ organization, partnerId }) {
    const history = useHistory();
    const { t } = useTranslation();
    const [formState, setFormState, replaceForm] = useForm(
        { name: '', inventories: [], rates: [], rate_templates: [], seats: [], autoBooking: true, zones: [], category_is_ga: false },
        { name: requiredField }
    );
    const toast = React.useContext(ToastContext);
    const [totalBatch, setTotalBatch] = React.useState(0);
    const [showModal, setShowModal] = React.useState(false);
    const [isSeated, setIsSeated] = React.useState(true);
    const [loading, setLoading] = React.useState(false);

    const [partner, loadingPartner] = PartnerModel.useApiModel(
        {
            id: partnerId,
            organizationId: organization.id,
            onError: e => {
                console.error(e);
                toast.error(t('ticket.partners.retrieve.error'));
            }
        },
        [partnerId, organization]
    );

    const createBatch = React.useCallback(() => {
        let { inventories, rates, rate_templates, seats, name, autoBooking, zones } = formState;
        let inventoriesIds = inventories.map(invt => invt.pk);
        let rates_to_map = isSeated ? rates : rates.filter(rate => rate.quantity);
        let partnerBatch = new PartnerBatchBooking({
            name,
            inventories: inventoriesIds,
            rates: rates_to_map.map(r => ({ rate: r.pk, quantity: r.quantity || 0 })),
            rate_templates: rate_templates.map(r => ({ rate_template: r.pk, quantity: r.quantity || 0 })),
            seats: seats.map(seat => seat.uuid),
            zones: zones
        });
        partnerBatch
            .save(
                {
                    organizationId: organization.id,
                    partnerId
                },
                { auto: isSeated ? autoBooking : true }
            )
            .then(() => {
                setLoading(false);
                toast.success(t('ticket.partners.batch.creationSuccess'));
                history.push(`/ticket/O${organization.id}/partners/${partnerId}/batches`);
            })
            .catch(e => {
                Sentry.captureException(e);
                setLoading(false);
                const error = e.parseErrorRecursive(t, 'partners.invitation.attendees_quickadd.');
                if (error) {
                    const split = error.split('\n');
                    const error_content = [];
                    error_content.push(t('ticket.partners.batch.creationFailureList'), <br />);
                    for (let line of split) {
                        error_content.push(line, <br />);
                    }
                    return toast.error(error_content);
                }
                toast.error(t('ticket.partners.batch.creationFailure'));
            });
    }, [formState, partnerId, organization, isSeated]);

    React.useEffect(() => {
        Sentry.configureScope(scope => {
            scope.setExtra('partner_id', partnerId);
        });
        PartnerBatchBooking.getAll({ organizationId: organization.id, partnerId }).then(bookings => {
            let count = bookings.__meta.headers.get('total-count');
            if (count) {
                setTotalBatch(count);
            }
        });
    }, []);

    const onCloseModal = React.useCallback(() => {
        setShowModal(false);
    });

    const onAcceptModal = React.useCallback(() => {
        setLoading(true);
        createBatch();
        setShowModal(false);
    });

    const disabledSubmit = React.useMemo(() => {
        return (
            (!formState.autoBooking && !formState.seats?.length && isSeated) ||
            (formState.autoBooking && !formState.zones.length && isSeated) ||
            (!isSeated && !formState.rates?.some(r => r.quantity) && !formState.rate_templates?.some(r => r.quantity)) ||
            partner?.isDeleted()
        );
    }, [isSeated, formState]);

    if (loading || loadingPartner) {
        return <Loading />;
    }

    return (
        <>
            <BatchModal
                show={showModal}
                onClose={onCloseModal}
                onAccept={onAcceptModal}
                labelReturn={t('ticket.partners.batch.modalLabelReturn')}
                labelAccept={t('ticket.partners.batch.modalLabelAccept')}
                body={t(isSeated ? 'ticket.partners.batch.modalBody' : 'ticket.partners.batch.modalBodyNonSeated', {
                    name: formState.name,
                    numberSeat: formState.seats.length !== 0 ? formState.seats.length : formState.zones.map(zone => zone.quantity).reduce((a, b) => a + b, 0),
                    nameRate: formState.rates.length ? formState.rates[0].resolved_name : formState.rate_templates.length ? formState.rate_templates[0].name : '',
                    numberEvent: formState.inventories.map(invt => invt.event).length,
                    number: formState.rates.reduce((sum, curr) => sum + (curr.quantity || 0), 0) + formState.rate_templates.reduce((sum, curr) => sum + (curr.quantity || 0), 0)
                })}
                title={t('ticket.partners.batch.modalTitle')}
            />
            <WeezHeader
                title={t('ticket.partners.batch.creationTitle')}
                backLink={{ onClick: () => history.push(`/ticket/O${organization.id}/partners/${partnerId}/batches`) }}
                rightComponent={[<Button primary key="primary" label={t('ticket.partners.batch.createButton')} disabled={disabledSubmit} onClick={() => setShowModal(true)} />]}
            >
                {t('ticket.partners.batch.creationSubTitle')}
            </WeezHeader>
            <Card.FormManager steps={isSeated ? null : ['done', 'done', 'open']}>
                <Card.FormStep
                    title={t('ticket.partners.batch.infos.title')}
                    subtitle={t('ticket.partners.batch.infos.description')}
                    editLabel={t('ticket.common.action.edit')}
                    onEdit={() => setIsSeated(true)}
                    summary={
                        formState.name !== '' ? (
                            <>
                                {t('ticket.partners.batch.infos.label')}: <b>{formState.name}</b>
                            </>
                        ) : null
                    }
                >
                    <PartnerBatchBookingsInfos
                        onChange={name => {
                            setFormState('name', name);
                        }}
                        name={formState.name}
                        countBatch={totalBatch}
                    />
                </Card.FormStep>
                <Card.FormStep
                    title={t('ticket.partners.batch.event.title')}
                    subtitle={t('ticket.partners.batch.event.description')}
                    editLabel={t('ticket.common.action.edit')}
                    onEdit={() => setIsSeated(true)}
                    summary={<HiddenText tradkey="ticket.partners.batch.event.hiddenText" values={formState.inventories} />}
                >
                    <PartnerBatchBookingsInventories
                        partnerId={partnerId}
                        organizationId={organization.id}
                        onChange={inventories => {
                            setFormState('inventories', inventories);
                        }}
                        inventories={formState.inventories}
                    />
                </Card.FormStep>
                <Card.FormStep
                    title={t('ticket.partners.batch.rate.title')}
                    editLabel={t('ticket.common.action.edit')}
                    summary={
                        <HiddenText
                            tradkey="ticket.partners.batch.rate.hiddenText"
                            values={formState.rates.length ? formState.rates : formState.rate_templates}
                            getterName={'resolved_name'}
                        />
                    }
                    subtitle={t('ticket.partners.batch.rate.description')}
                >
                    <PartnerBatchBookingsRates
                        partnerId={partnerId}
                        organizationId={organization.id}
                        currency={organization.currency}
                        onChange={({ rates, rate_templates }, category_is_ga) => {
                            let form = { ...formState };
                            form.rates = rates;
                            form.rate_templates = rate_templates;
                            form.zones = [];
                            form.category_is_ga = category_is_ga;
                            replaceForm(form);
                        }}
                        inventories={formState.inventories}
                        ratesProp={[...formState.rate_templates, ...formState.rates]}
                        onSeated={setIsSeated}
                        isSeated={isSeated}
                    />
                </Card.FormStep>
                {isSeated && (
                    <Card.FormStep
                        title={
                            <span>
                                {t('ticket.partners.batch.seat.title')}{' '}
                                <TooltipHelp
                                    style={{ fontWeight: 'normal' }}
                                    title={formState.category_is_ga ? t('ticket.partners.batch.seat.description_ga') : t('ticket.partners.batch.seat.description')}
                                />
                            </span>
                        }
                        editLabel={t('ticket.common.action.edit')}
                    >
                        <PartnerBatchBookingsSeat
                            partnerId={partnerId}
                            rates={[...formState.rates, ...formState.rate_templates]}
                            rate_category_is_ga={formState.category_is_ga}
                            inventories={formState.inventories}
                            onSeatsSelected={seats => {
                                setFormState('seats', seats);
                            }}
                            autoBooking={formState.autoBooking}
                            setAutoBooking={value => setFormState('autoBooking', value)}
                            setAutoBookingQuantity={value => setFormState('zones', value)}
                            autoBookingQuantity={formState.zones}
                        />
                    </Card.FormStep>
                )}
            </Card.FormManager>
        </>
    );
}

PartnerBatchBookings.propTypes = {
    organization: PropTypes.object.isRequired,
    partnerId: PropTypes.string.isRequired
};
